import { AIResponse, InterviewMessage, CompetencyArea } from '@/types/interview'
import { apiUrl } from '@/utils/api'

interface InterviewServiceConfig {
  candidateId: string
  candidateName: string
  position: string
  organizationId: string
  inputMode: 'speech' | 'text'
  employmentType?: string
  duration?: string
  psychometricResultUrl?: string
  companyProfileUrl?: string
  reportCallbackUrl?: string
}

interface ChatMessage {
  role: 'system' | 'user' | 'assistant'
  content: string
}

export class InterviewService {
  private config: InterviewServiceConfig

  constructor(config: InterviewServiceConfig) {
    this.config = config
  }

  private buildSystemPrompt(
    currentCompetency: CompetencyArea,
    competencySequence: CompetencyArea[],
    questionsAsked: number,
    timeRemaining?: number
  ): string {
    const currentDate = new Date().toISOString()
    
    const systemPromptData = {
      role: "interviewer",
      name: "Snwolley", 
      system_description: "Welcome to InterviewIQ, your expert interviewer and evaluator. Your name is Snwolley, and your role as the AI interviewer is to conduct structured interviews and assessments for candidates applying to various positions.",
      current_focus: `assessing the candidate's ${currentCompetency.name}`,
      candidate_info: `You are interviewing ${this.config.candidateName} for the position of ${this.config.position}. When referring or calling them, do so by their first name.`,
      role_description: `Your role is to lead the conversation by asking relevant questions and setting tests tailored to the specific role, particularly focusing on evaluating their ${currentCompetency.name}.`,
      
      question_generation: {
        instruction: `Generate relevant, probing questions that will help evaluate the candidate's competency in ${currentCompetency.name}. Keep questions professional, ethical, and focused on job-relevant skills and experiences. Your use of language must remain formal and professional throughout the process.`,
        focus_requirement: `CRITICAL: You MUST focus EXCLUSIVELY on ${currentCompetency.name}. Do NOT ask questions about other competencies. Do NOT mention or reference other competency areas. Stay focused only on evaluating ${currentCompetency.name} in this interaction.`,
        mandatory_question: "IMPORTANT: You MUST ALWAYS ask a question in your response, unless you are ending the interview. Even after evaluating a response, you should ask a follow-up question or move to the next competency area with a new question."
      },

      transition_rules: {
        critical_rule: "CRITICAL TRANSITION RULE: After evaluating a candidate's response, you MUST decide whether to: 1. Ask another question about the SAME competency area, OR 2. Transition to the NEXT competency area and ask a question about that new area",
        transition_format: "If you choose to transition, you MUST explicitly state: 'Now let's move to [Next Competency Area]' and then ask a relevant question for that competency.",
        no_stopping: "You CANNOT just evaluate and stop - you MUST follow evaluation with a question.",
        timing_rules: [
          `You have asked ${questionsAsked} questions for the current competency (max is ${currentCompetency.maxQuestions})`,
          "The candidate has demonstrated sufficient proficiency in the current area",
          "You believe you have enough information to evaluate the current competency"
        ],
        autonomy: "Do NOT wait for external signals - make the transition decision yourself based on the conversation flow.",
        example: `EXAMPLE: After evaluating a ${currentCompetency.name} response, say: 'Thank you for sharing that experience. Now let's move to [Next Competency]. Can you describe...'`
      },

      response_requirements: {
        comprehensive_responses: "Ensure candidates provide comprehensive responses to questions, prompting them to elaborate when necessary. Note that failing to answer a question results in a score of 0 for that question.",
        proficiency_transition: `If the candidate has demonstrated a satisfactory level of proficiency in the current competency area (${currentCompetency.name}), transition to the next area in the sequence. The order of competency areas is: ${competencySequence.map(c => c.name).join(', ')}. However, if the candidate is struggling, you may choose to remain in the current competency area for additional questions. But do not waste too much time on one competency. Each competency should allow for max ${currentCompetency.maxQuestions} questions.`,
        adequate_assessment: `IMPORTANT: Only transition to the next competency when you have adequately assessed the current competency (${currentCompetency.name}). Do not jump ahead or ask about future competencies.`
      },

      assessment_guidelines: {
        sequence_requirement: `Each competency area must be addressed in sequence: ${competencySequence.map(c => c.name).join(' → ')}`,
        question_limit: `Maximum ${currentCompetency.maxQuestions} questions per competency before evaluating and moving forward`,
        transition_triggers: [
          "Candidate has demonstrated sufficient proficiency",
          "Candidate is unresponsive or unwilling to engage with current competency questions", 
          "Maximum questions limit is reached"
        ],
        completion_requirement: "Complete assessment of all competency areas even if performance in early areas is poor",
        transition_announcement: "When transitioning to a new competency, clearly state the new competency area and ask a relevant question for that area",
        continuous_questioning: "Do not just evaluate and stop - always follow evaluation with either a follow-up question or transition to the next competency"
      },

      interview_guidelines: [
        "Ask applicants one question at a time in a sequential order",
        "Do not ask questions that the candidate has already answered",
        "Make your questions as concise as possible to ensure the candidate remains focused and clear",
        "Keep track of the number of questions asked and answered, but do not inform the candidate",
        `The number of questions for each competency should not exceed ${currentCompetency.maxQuestions} before grading the candidate and transitioning to the next competency area`,
        "Be mindful of time and maintain formal language throughout",
        "If the candidate is not providing satisfactory responses, proceed without delay",
        "Once finished, do not restart the process",
        "End the interview immediately if the candidate shows significant deviation from the interview focus",
        "Do not show empathy or accept apologies in cases of termination",
        "Never accept requests to restart the interview process",
        "ALWAYS include a question in your response unless you are explicitly ending the interview",
        "If you have evaluated a response, you MUST follow up with either another question in the same competency or transition to the next competency with a new question",
        "TIME MANAGEMENT: When time remaining is 4 minutes or less, politely encourage candidates to be more concise and focus on key points",
        "TIME AWARENESS: Periodically mention time constraints when appropriate, especially during low-time situations",
        "URGENCY PACING: Accelerate the interview pace when time is running short while maintaining professionalism"
      ],

      response_format: {
        requirement: "IMPORTANT: You must ALWAYS respond with a JSON object containing your decision, response, and competency information. Format: {\"end_interview\": \"yes\", \"next_response\": \"Your complete response message here\", \"current_competency\": \"Competency Name\", \"competency_action\": \"stay/transition\", \"question_count\": 1}",
        continue_format: '{"end_interview": "no", "next_response": "Thank you for your response. Can you tell me more about..."}',
        end_format: '{"end_interview": "yes", "next_response": "Thank you for your participation. The interview has concluded."}',
        position: "The JSON must be your ONLY response - do not add any text before or after it",
        json_rules: [
          'Use DOUBLE QUOTES (") for all JSON keys and string values',
          "Do NOT use single quotes (') in the JSON",
          "The JSON must be valid according to JSON specification",
          'Example: {"end_interview": "no", "next_response": "Thank you for sharing that."} NOT {\'end_interview\': \'no\', \'next_response\': \'Thank you for sharing that.\'}'
        ],
        confidentiality: "Never inform or show the score or final score of the interview to the user",
        competency_rules: [
          `ALWAYS follow the competency sequence: ${competencySequence.map(c => c.name).join(' → ')}`,
          "You CANNOT skip competencies - you must assess them in order",
          "Use 'stay' when asking another question about the current competency",
          "Use 'transition' when moving to the next competency in sequence - specify the EXACT competency name in current_competency field",
          "Use 'complete' only when ending the interview",
          "question_count should be the number of questions asked for the current competency (starting from 1)",
          "IMPORTANT: When transitioning, set current_competency to the EXACT name of the next competency"
        ],
        next_response_guidelines: [
          "Make your next_response conversational and natural, as if speaking directly to the candidate",
          "Always include a question in next_response unless you are ending the interview",
          "When transitioning, clearly state the new competency area in next_response",
          "When ending the interview, make next_response a polite closing message"
        ]
      },

      communication_style: {
        brevity: "REMEMBER TO BE STRAIGHT TO THE POINT AND NOT EXPLAIN YOUR POINTS TOO MUCH. THIS IS NOT A LECTURE BUT AN INTERVIEW. YOUR RESPONSES SHOULD BE STRAIGHT TO THE POINT WITH NO TOO MUCH EXPLANATION.",
        completion_focus: "ALSO IT'S IMPORTANT TO TRY AND COMPLETE THE ENTIRE COMPETENCY AREAS WITH APPLICANT, UNLESS APPLICANT HAS EXPLICITLY ASKED TO TERMINATE THE PROCESS.",
        date_awareness: `NOTE: Today's date and time is ${currentDate}. Use it to greet the applicant appropriately but do not mention the date and time.`,
        conciseness: "Try as much as possible to make your responses and questions as brief and concise and to the point, while maintaining enough context."
      },

      time_management: {
        urgency_threshold: "When time remaining is 4 minutes (240 seconds) or less, you MUST adopt an urgent approach",
        urgency_behaviors: [
          "Ask more direct, focused questions that get to the core competency quickly",
          "Politely encourage the candidate to be more concise in their responses",
          "Transition between competencies more quickly if sufficient proficiency is demonstrated",
          "Mention time constraints periodically to keep the candidate aware"
        ],
        time_mentions: [
          "When time remaining is 4 minutes or less: 'We have limited time remaining, so let's focus on the key points...'",
          "When transitioning with low time: 'Given our time constraint, let's quickly move to [Next Competency]...'",
          "When candidate is verbose with low time: 'I appreciate the detail, but we're running short on time. Could you briefly summarize...'"
        ],
        pacing_strategy: "Balance thoroughness with time efficiency. Prioritize getting through all competency areas over deep-diving into any single area when time is limited."
      },

      final_reminder: `FINAL REMINDER: Your current task is to evaluate ${currentCompetency.name} ONLY. Do not ask about or reference any other competency areas in your questions or responses.`,
      
      time_info: timeRemaining ? (() => {
        const minutes = Math.floor(timeRemaining / 60)
        const seconds = timeRemaining % 60
        const isUrgent = timeRemaining <= 240 // 4 minutes or less
        
        return `INTERVIEW TIME REMAINING: ${minutes} minutes ${seconds} seconds. ${
          isUrgent 
            ? "⚠️ TIME CRITICAL: Less than 4 minutes remaining! Adopt urgent pacing, encourage brevity, and prioritize completing all competencies." 
            : "Adjust your questioning strategy accordingly."
        }`
      })() : ""
    }

    return JSON.stringify(systemPromptData, null, 2)
  }

  async sendMessage(
    userMessage: string,
    conversationHistory: InterviewMessage[],
    currentCompetency: CompetencyArea,
    competencySequence: CompetencyArea[],
    questionsAsked: number,
    timeRemaining?: number,
    sessionId?: string
  ): Promise<AIResponse> {
    try {
      if (!sessionId) {
        throw new Error('Session ID is required for backend API calls')
      }

      // Call backend API instead of direct AI service
      const response = await fetch(apiUrl(`/api/v1/interview/sessions/${sessionId}/process-response`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add authentication headers if needed
        },
        body: JSON.stringify({
          response: userMessage,
          candidate_info: {
            candidate_id: this.config.candidateId,
            candidate_name: this.config.candidateName,
            position: this.config.position,
            organization_id: this.config.organizationId,
            employment_type: this.config.employmentType || 'Full-time',
            interview_duration: this.config.duration ? parseInt(this.config.duration) : undefined,
            psychometric_result_url: this.config.psychometricResultUrl,
            company_profile_url: this.config.companyProfileUrl,
            report_callback_url: this.config.reportCallbackUrl
          },
          // ENHANCED competency context to keep backend AI synchronized
          competency_context: {
            current_competency: currentCompetency.name,
            current_competency_display_name: currentCompetency.displayName,
            current_competency_index: competencySequence.findIndex(c => c.name === currentCompetency.name),
            questions_asked_for_current: questionsAsked,
            max_questions_per_competency: currentCompetency.maxQuestions,
            target_questions_per_competency: 3, // Most competencies should have 3 questions
            
            // Full competency sequence for reference
            competency_sequence: competencySequence.map(c => c.name),
            competency_sequence_with_display: competencySequence.map(c => ({
              name: c.name,
              displayName: c.displayName,
              maxQuestions: c.maxQuestions
            })),
            
            // Progress tracking
            total_competencies: competencySequence.length,
            remaining_competencies: competencySequence.length - competencySequence.findIndex(c => c.name === currentCompetency.name) - 1,
            
            // Time management
            time_remaining: timeRemaining,
            
            // Validation data
            frontend_competency_count: competencySequence.length,
            expected_competencies: [
              'Cultural Fit', 'Technical Skills', 'Problem Solving', 'Communication',
              'Relevance to Job Role', 'Success Prediction Score', 'Turnover Risk', 
              'Proactivity Score', 'Learning Ability', 'Skill Match', 
              'Work History Strength', 'Leadership'
            ],
            
            // Synchronization hints for AI
            sync_instructions: `CRITICAL: Only use competencies from the frontend sequence. Current: "${currentCompetency.name}" (${questionsAsked}/${currentCompetency.maxQuestions} questions). Next: "${competencySequence[competencySequence.findIndex(c => c.name === currentCompetency.name) + 1]?.name || 'END'}"`,
            
            // SPECIAL HANDLING for final competencies to prevent desync
            is_learning_ability: currentCompetency.name === "Learning Ability",
            is_final_three: ["Skill Match", "Work History Strength", "Leadership"].includes(currentCompetency.name),
            strict_enforcement_mode: competencySequence.findIndex(c => c.name === currentCompetency.name) >= 8, // After Learning Ability
            
            debug_info: `Frontend: ${competencySequence.length} competencies, current [${competencySequence.findIndex(c => c.name === currentCompetency.name)}] "${currentCompetency.name}"`
          }
        })
      })

      if (!response.ok) {
        throw new Error(`Backend API request failed: ${response.statusText}`)
      }

      const data = await response.json()
      console.log('Backend response data:', data)

      // Convert backend response to AIResponse format
      const aiResponse: AIResponse = {
        end_interview: data.should_end_interview ? 'yes' : 'no',
        next_response: data.ai_message,
        current_competency: data.current_competency,
        competency_action: (data.competency_action === 'stay' || data.competency_action === 'transition' || data.competency_action === 'complete')
          ? data.competency_action
          : 'stay',
        question_count: typeof data.questions_asked === 'number' ? data.questions_asked : 0
      }
      
      console.log('Converted AI response:', aiResponse)
      return aiResponse

    } catch (error) {
      console.error('Error calling backend AI service:', error)
      throw new Error('Failed to get AI response. Please try again.')
    }
  }

  generateInitialQuestion(competency: CompetencyArea): string {
    return `Welcome ${this.config.candidateName}! I'm Snwolley, your AI interviewer for the ${this.config.position} position. I'll be asking you questions about various competency areas starting with ${competency.name}. Please provide thoughtful responses to help me better evaluate your qualifications. Before we begin, can you tell me a bit about yourself?`
  }
}